import type { ChecklistRowSave } from '@/api/hooks';

// SS10 / item 18: shared by Submitted Forms (list) and the View Form page. Every
// submitted client / engagement row carries a checklist the admins work through
// (Yes/No per system + Date Completed).

type Row = Record<string, unknown>;

// The form types (DB value -> label).
export const FORM_TYPES: { value: string; label: string }[] = [
  { value: 'Termination', label: 'Client Termination' },
  { value: 'Reactivation', label: 'Client Reactivation' },
  { value: 'Discontinuation', label: 'Engagement Discontinuation' },
  // Mass Change: add here (and to a tab) once its spec is settled.
];
export const typeLabel = (t: string) => FORM_TYPES.find((f) => f.value === t)?.label ?? t;

// Checklist items per form type (DB column -> API field -> column header).
export type ItemCol = 'Maconomy' | 'Constant' | 'ClientManager' | 'ReturnManager' | 'ClientProfile' | 'Workflow' | 'CCHEng' | 'EL';
export const API_FIELD: Record<ItemCol, keyof ChecklistRowSave> = {
  Maconomy: 'maconomy', Constant: 'constant', ClientManager: 'clientManager', ReturnManager: 'returnManager',
  ClientProfile: 'clientProfile', Workflow: 'workflow', CCHEng: 'cchEng', EL: 'el',
};
export const LABEL: Record<ItemCol, string> = {
  Maconomy: 'Maconomy', Constant: 'Constant', ClientManager: 'Client Manager', ReturnManager: 'Return Mgr',
  ClientProfile: 'Client Profile', Workflow: 'Workflow', CCHEng: 'CCH Eng', EL: 'EL',
};
// Dropdown options mirror Pending Requests: most items are '— / Yes / No / N/A'
// (blank to start); the client forms' Client Manager / Return Manager are
// 'Yes / No / N/A' starting at 'No'.
const OPT_BLANK = ['', 'Yes', 'No', 'N/A'];
const OPT_REQ = ['Yes', 'No', 'N/A'];
export const isClientForm = (t: string) => t === 'Termination' || t === 'Reactivation';
const isReqItem = (t: string, c: ItemCol) => isClientForm(t) && (c === 'ClientManager' || c === 'ReturnManager');
export const optionsFor = (t: string, c: ItemCol) => (isReqItem(t, c) ? OPT_REQ : OPT_BLANK);
const defaultFor = (t: string, c: ItemCol) => (isReqItem(t, c) ? 'No' : '');
// Same "needs a Date Completed" rule as Pending: once the tracked items are all
// Yes or N/A (client forms: Maconomy + Constant; others: every item).
export const DONE = ['Yes', 'N/A'];
const CLIENT_ITEMS: ItemCol[] = ['Maconomy', 'Constant', 'ClientManager', 'ReturnManager'];
export const ITEMS: Record<string, ItemCol[]> = {
  Termination: CLIENT_ITEMS,
  Reactivation: CLIENT_ITEMS,
  Discontinuation: ['Maconomy', 'ClientProfile', 'ReturnManager', 'Workflow', 'CCHEng', 'EL'],
  MassChange: ['Maconomy', 'ClientProfile', 'ReturnManager', 'Workflow', 'CCHEng', 'EL', 'Constant'],
};
export const trackedItems = (formType: string): ItemCol[] =>
  isClientForm(formType) ? ['Maconomy', 'Constant'] : (ITEMS[formType] ?? []);
export const DATE_MSG = 'All tracked items are Yes / N/A: enter the Date Completed before saving.';

export interface Detail {
  key: string;
  detailType: 'Client' | 'Engagement';
  detailId: number;
  clientId: string;
  clientName: string;
  engagementId: string;
  service: string;
  effectiveDate: string;    // MM/DD/YYYY for display
  parentClientId: string;   // Termination: sub-client picked under its master
  newMaster: string;        // Termination: new master for the staying sub-clients
  values: Partial<Record<ItemCol | 'DateCompleted', string>>; // DateCompleted stays YYYY-MM-DD (date input)
  updatedBy: string;
}
export interface Grouped {
  id: number;
  formType: string;
  by: string;
  email: string;
  date: string;             // submitted, MM/DD/YYYY h:mm
  notes: string;
  termReason: string;       // Termination only (item 15)
  crunchafi: string;
  emailed: boolean;
  completed: boolean;       // moved to Completed (stored as IsArchived)
  completedBy: string;
  completedAt: string;      // MM/DD/YYYY h:mm (Eastern)
  details: Detail[];
}

// SubmittedDateUtc is UTC; show it in Eastern time for everyone (same as Pending Requests).
export const fmtDateTime = (v: unknown) =>
  v ? new Date(String(v)).toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
/** YYYY-MM-DD -> MM/DD/YYYY for display. */
export const mdy = (v: unknown) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};
const isoDate = (v: unknown) => (v ? String(v).slice(0, 10) : '');

/** API rows (one per submission x detail row) -> one Grouped per submission, newest first. */
export function groupSubmissions(rows: Row[]): Grouped[] {
  const map = new Map<number, Grouped>();
  for (const r of rows) {
    const id = Number(r.SubmissionID);
    let g = map.get(id);
    if (!g) {
      g = {
        id,
        formType: String(r.FormType ?? ''),
        by: String(r.SubmittedBy ?? ''),
        email: String(r.SubmittedByEmail ?? ''),
        date: fmtDateTime(r.SubmittedDateUtc),
        notes: String(r.Notes ?? ''),
        termReason: r.TerminationReason
          ? `${r.TerminationReason}${r.TerminationReasonOther ? ` (${r.TerminationReasonOther})` : ''}` : '',
        crunchafi: String(r.CrunchafiDisable ?? ''),
        emailed: !!r.AdminEmailedUtc,
        completed: !!r.IsArchived,
        completedBy: r.IsArchived ? String(r.ArchivedBy ?? '') : '',
        completedAt: r.IsArchived ? fmtDateTime(r.ArchivedUtc) : '',
        details: [],
      };
      map.set(id, g);
    }
    if (r.DetailId != null && (r.DetailType === 'Client' || r.DetailType === 'Engagement')) {
      const values: Detail['values'] = { DateCompleted: isoDate(r.DateCompleted) };
      (Object.keys(API_FIELD) as ItemCol[]).forEach((c) => {
        values[c] = (r[c] as string) || defaultFor(String(r.FormType ?? ''), c);
      });
      g.details.push({
        key: `${id}|${r.DetailType}|${r.DetailId}`,
        detailType: r.DetailType as Detail['detailType'],
        detailId: Number(r.DetailId),
        clientId: String(r.ClientID ?? ''),
        clientName: String(r.ClientName ?? ''),
        engagementId: String(r.EngagementID ?? ''),
        service: String(r.Service ?? ''),
        effectiveDate: mdy(r.EffectiveDate),
        parentClientId: String(r.ParentClientID ?? ''),
        newMaster: r.NewMasterClientID ? `${r.NewMasterClientID} ${r.NewMasterClientName ?? ''}`.trim() : '',
        values,
        updatedBy: String(r.ChecklistUpdatedBy ?? ''),
      });
    }
  }
  return [...map.values()];
}

/** A row's tracked items are all Yes / N/A (only then can it take a Date Completed, like Pending). */
export const itemsDone = (formType: string, values: Detail['values']) =>
  trackedItems(formType).every((c) => DONE.includes(values[c] ?? ''));
/**
 * Clients in display order: each master (or stand-alone client) followed by the
 * sub-clients terminated under it. Used by the list and the View Form page.
 */
export function orderedClients(g: Grouped): { d: Detail; isSub: boolean }[] {
  const subsOf = new Map<string, Detail[]>();
  const top: Detail[] = [];
  for (const d of g.details) {
    if (d.parentClientId) subsOf.set(d.parentClientId, [...(subsOf.get(d.parentClientId) ?? []), d]);
    else top.push(d);
  }
  const out: { d: Detail; isSub: boolean }[] = [];
  for (const t of top) {
    out.push({ d: t, isSub: false });
    for (const s of subsOf.get(t.clientId) ?? []) out.push({ d: s, isSub: true });
    subsOf.delete(t.clientId);
  }
  for (const rest of subsOf.values()) for (const s of rest) out.push({ d: s, isSub: true }); // master not on this request
  return out;
}
